import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Flatpickr from 'react-flatpickr';
import HeroBusImage from '../assets/img/hero-bus.jpg';
import 'flatpickr/dist/themes/material_green.css';
import { api } from '../../api';

const BookingForm = ({ date, fromData, toData, modify = false, onModifySearch }) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        from: fromData?.displayName ?? '',
        to: toData?.displayName ?? '',
        date: date,
        fromData: fromData || {},
        toData: toData || {},
    });

    const formatDate = (date) => {
        const day = date.getDate();
        const month = date.toLocaleString('default', { month: 'short' });
        const year = date.getFullYear();
        return `${day} ${month}, ${year}`;
    };

    const [activeDate, setActiveDate] = useState('today');
    const [suggestions, setSuggestions] = useState({
        from: {
            popular: [],
            search: [],
            recent: []
        },
        to: {
            popular: [],
            search: [],
            recent: []
        }
    });
    const [showSuggestions, setShowSuggestions] = useState({
        from: false,
        to: false
    });
    const [loading, setLoading] = useState({
        from: false,
        to: false
    });
    const [hasTyped, setHasTyped] = useState({
        from: false,
        to: false
    });
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [recentSearches, setRecentSearches] = useState([]);
    const hasFetched = useRef(false);
    const dateInputRef = useRef(null);
    const flatpickrRef = useRef(null);

    // Load recent searches from localStorage on component mount
    useEffect(() => {
        const loadRecentSearches = () => {
            const savedRecentSearches = localStorage.getItem('recentSearches');
            if (savedRecentSearches) {
                try {
                    const parsedSearches = JSON.parse(savedRecentSearches);
                    // Sort by timestamp (most recent first)
                    const sortedSearches = parsedSearches.sort((a, b) => 
                        (b.timestamp || 0) - (a.timestamp || 0)
                    );
                    setRecentSearches(sortedSearches);
                    
                    // Update suggestions with recent searches
                    setSuggestions(prev => ({
                        from: {
                            ...prev.from,
                            recent: sortedSearches.map(search => ({
                                id: search.id,
                                displayName: search.fromName || search.from,
                                original_name: search.from,
                                city: search.fromCity,
                                type: search.typeFrom || 'city',
                                isRecent: true,
                                searchData: search
                            }))
                        },
                        to: {
                            ...prev.to,
                            recent: sortedSearches.map(search => ({
                                id: search.id,
                                displayName: search.toName || search.to,
                                original_name: search.to,
                                city: search.toCity,
                                type: search.typeTo || 'city',
                                isRecent: true,
                                searchData: search
                            }))
                        }
                    }));
                } catch (error) {
                    console.error('Error parsing recent searches:', error);
                }
            }
        };

        loadRecentSearches();
    }, []);

    // Save search to recent searches
    const saveToRecentSearches = (searchData) => {
        const newSearch = {
            id: `${searchData.from}_${searchData.to}_${searchData.date}_${Date.now()}`,
            from: searchData.from,
            to: searchData.to,
            fromName: searchData.fromName,
            toName: searchData.toName,
            fromCity: searchData.fromCity,
            toCity: searchData.toCity,
            typeFrom: searchData.typeFrom,
            typeTo: searchData.typeTo,
            date: searchData.date,
            timestamp: new Date().getTime()
        };

        // Get existing searches
        let searches = [];
        const savedSearches = localStorage.getItem('recentSearches');
        if (savedSearches) {
            try {
                searches = JSON.parse(savedSearches);
            } catch (error) {
                console.error('Error parsing recent searches:', error);
                searches = [];
            }
        }

        // Remove duplicates by checking same from, to and date
        searches = searches.filter(search => 
            !(search.from === newSearch.from && 
              search.to === newSearch.to && 
              search.date === newSearch.date)
        );

        // Add new search at beginning
        searches.unshift(newSearch);

        // Keep only last 5 searches
        if (searches.length > 5) {
            searches = searches.slice(0, 5);
        }

        // Save to localStorage
        localStorage.setItem('recentSearches', JSON.stringify(searches));
        setRecentSearches(searches);

        // Update suggestions with recent searches
        setSuggestions(prev => ({
            from: {
                ...prev.from,
                recent: searches.map(search => ({
                    id: search.id,
                    displayName: search.fromName || search.from,
                    original_name: search.from,
                    city: search.fromCity,
                    type: search.typeFrom || 'city',
                    isRecent: true,
                    searchData: search
                }))
            },
            to: {
                ...prev.to,
                recent: searches.map(search => ({
                    id: search.id,
                    displayName: search.toName || search.to,
                    original_name: search.to,
                    city: search.toCity,
                    type: search.typeTo || 'city',
                    isRecent: true,
                    searchData: search
                }))
            }
        }));
    };

    useEffect(() => {
        const today = new Date();
        const formattedDate = formatDate(today);
        setFormData(prev => ({ ...prev, date: formattedDate }));
        setSelectedDate(today);
    }, []);

    const searchTimeoutRef = useRef(null);
    const fromInputRef = useRef(null);
    const toInputRef = useRef(null);
    const fromSuggestionsRef = useRef(null);
    const toSuggestionsRef = useRef(null);
    const [popularCities, setPopularCities] = useState([]);

    // Fetch search suggestions from API
    const fetchSuggestions = async (query, type) => {
        if (!query || query.trim().length < 2) {
            setSuggestions(prev => ({
                ...prev,
                [type]: {
                    popular: prev[type].popular,
                    search: [],
                    recent: prev[type].recent
                }
            }));
            return;
        }

        setLoading(prev => ({ ...prev, [type]: true }));

        try {
            const response = await fetch(
                `${api.searchQuery}?query=${encodeURIComponent(query)}&type=${type}`
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            let sortedData = [];
            if (data.data && data.data.stands) {
                const pickupDrops = [];
                const standsOnly = [];

                data.data.stands.forEach(stand => {
                    if (stand.pickupDrops && stand.pickupDrops.length > 0) {
                        stand.pickupDrops.forEach(pickupDrop => {
                            pickupDrops.push({
                                ...stand,
                                displayName: pickupDrop.original_name || pickupDrop.name,
                                original_name: pickupDrop.original_name,
                                pickupDropName: pickupDrop.name,
                                pickupDropType: pickupDrop.type,
                                isPickupDrop: true
                            });
                        });
                        standsOnly.push({
                            ...stand,
                            displayName: stand.original_name || stand.name,
                            pickupDropType: stand.type,
                            isPickupDrop: false
                        });
                    } else {
                        standsOnly.push({
                            ...stand,
                            displayName: stand.original_name || stand.name,
                            isPickupDrop: false,
                            pickupDropType: stand.type,
                        });
                    }
                });

                // Combine with pickupDrops first
                sortedData = [...pickupDrops, ...standsOnly];
            }

            setSuggestions(prev => ({
                ...prev,
                [type]: {
                    popular: prev[type].popular,
                    search: sortedData,
                    recent: prev[type].recent
                }
            }));
        } catch (error) {
            setSuggestions(prev => ({
                ...prev,
                [type]: {
                    popular: prev[type].popular,
                    search: [],
                    recent: prev[type].recent
                }
            }));
        } finally {
            setLoading(prev => ({ ...prev, [type]: false }));
        }
    };

    // Debounced search function
    const handleSearch = (query, type) => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            fetchSuggestions(query, type);
        }, 300);
    };

    const handleInputChange = (type, value) => {
        setHasTyped(prev => ({ ...prev, [type]: true }));
        setFormData(prev => ({
            ...prev,
            [type]: value,
            [`${type}Data`]: null
        }));

        if (value && value.trim().length >= 2) {
            handleSearch(value, type);
        } else {
            setSuggestions(prev => ({
                ...prev,
                [type]: {
                    popular: prev[type].popular,
                    search: [],
                    recent: prev[type].recent
                }
            }));
        }
    };

    const handleSuggestionSelect = (type, suggestion) => {
        // Check if this is a recent search item
        if (suggestion.isRecent) {
            // For recent searches, use the searchData
            const searchData = suggestion.searchData || suggestion;
            const isFrom = type === 'from';
            
            const dataToStore = {
                ...suggestion,
                original_name: isFrom ? searchData.from : searchData.to,
                displayName: isFrom ? searchData.fromName : searchData.toName,
                city: isFrom ? searchData.fromCity : searchData.toCity,
                type: isFrom ? searchData.typeFrom : searchData.typeTo,
                pickupDropType: isFrom ? searchData.typeFrom : searchData.typeTo,
                isRecent: true
            };

            setFormData(prev => ({
                ...prev,
                [type]: dataToStore.displayName,
                [`${type}Data`]: dataToStore
            }));

            // Also update the other field if it's a recent search
            if (searchData) {
                const otherType = type === 'from' ? 'to' : 'from';
                const otherData = {
                    original_name: otherType === 'from' ? searchData.from : searchData.to,
                    displayName: otherType === 'from' ? searchData.fromName : searchData.toName,
                    city: otherType === 'from' ? searchData.fromCity : searchData.toCity,
                    type: otherType === 'from' ? searchData.typeFrom : searchData.typeTo,
                    pickupDropType: otherType === 'from' ? searchData.typeFrom : searchData.typeTo,
                    isRecent: true
                };

                setFormData(prev => ({
                    ...prev,
                    [otherType]: otherData.displayName,
                    [`${otherType}Data`]: otherData
                }));
            }
        } else {
            // Original logic for regular suggestions
            const displayName = suggestion.pickupDropName ||
                suggestion.displayName ||
                suggestion.name ||
                suggestion;

            const dataToStore = {
                ...suggestion,
                original_name: suggestion.original_name || suggestion.displayName || suggestion.name,
                displayName: displayName,
                city: suggestion.city || suggestion.name || displayName,
            };
            
            if (!suggestion.type) {
                dataToStore.type = 'city';
                dataToStore.dataType = 'city';
                dataToStore.dataCity = suggestion.name || displayName;
            }

            setFormData(prev => ({
                ...prev,
                [type]: displayName,
                [`${type}Data`]: dataToStore
            }));
        }

        setShowSuggestions(prev => ({ ...prev, [type]: false }));
        setHasTyped(prev => ({ ...prev, [type]: false }));
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check all elements that should NOT trigger closing
            const shouldNotClose = [
                fromInputRef.current,
                fromSuggestionsRef.current,
                toInputRef.current,
                toSuggestionsRef.current,
                dateInputRef.current,
                document.querySelector('.flatpickr-calendar')
            ].some(element =>
                element && element.contains && element.contains(event.target)
            );

            if (shouldNotClose) {
                return;
            }

            // Close suggestions
            setShowSuggestions({ from: false, to: false });
            setHasTyped({ from: false, to: false });
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        const formatDateForURL = (dateString) => {
            const date = new Date(dateString);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        const formattedDate = formatDateForURL(formData.date);

        const searchData = {
            from: formData.fromData.original_name || formData.fromData.name || formData.from,
            to: formData.toData.original_name || formData.toData.name || formData.to,
            fromCity: formData.fromData.city || formData.fromData.name || formData.from,
            toCity: formData.toData.city || formData.toData.name || formData.to,
            typeFrom: formData.fromData.pickupDropType || formData.fromData.type || 'city',
            typeTo: formData.toData.pickupDropType || formData.toData.type || 'city',
            fromName: formData.fromData.displayName || formData.from,
            toName: formData.toData.displayName || formData.to,
            date: formattedDate
        };

        const queryParams = new URLSearchParams({
            from: searchData.from,
            to: searchData.to,
            fromCity: searchData.fromCity,
            toCity: searchData.toCity,
            typeFrom: searchData.typeFrom,
            typeTo: searchData.typeTo,
            fromName: searchData.fromName,
            toName: searchData.toName,
            date: formattedDate
        }).toString();

        // Save to recent searches
        saveToRecentSearches(searchData);

        if (modify) {
            onModifySearch(searchData);
        } else {
            navigate(`/buses?${queryParams}`);
        }
    };

    const swapLocations = () => {
        setFormData({
            ...formData,
            from: formData.to,
            to: formData.from,
            fromData: formData.toData,
            toData: formData.fromData
        });
    };

    const handleDateChange = (selectedDates) => {
        if (selectedDates && selectedDates[0]) {
            const selectedDate = selectedDates[0];
            setSelectedDate(selectedDate);
            setFormData(prev => ({ ...prev, date: formatDate(selectedDate) }));
            setActiveDate('custom');
        }
    };

    const dateOptions = {
        minDate: 'today',
        dateFormat: 'd M, Y',
        disableMobile: true,
        defaultDate: selectedDate,
        clickOpens: true,
        allowInput: false,
    };

    const SearchSuggestionItem = ({ suggestion, type, index }) => {
        return (
            <div
                key={`${type}-search-${index}`}
                className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors duration-150 border-b border-gray-100 last:border-b-0"
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleSuggestionSelect(type, suggestion);
                }}
                data-type={suggestion.type}
                data-city={suggestion.city}
            >
                <div className="flex justify-between items-start">
                    <div className="flex items-start flex-1">
                        <i className={`fas fa-map-marker-alt mt-1 mr-3 ${type === 'from' ? 'text-blue-500' : 'text-orange-500'}`}></i>
                        <div className="flex-1">
                            <p className="font-medium text-gray-800 text-sm">
                                {suggestion.displayName || suggestion.original_name || suggestion.name}
                            </p>

                            {(suggestion.city || suggestion.state) && (
                                <p className="text-xs text-gray-600 mt-1">
                                    {suggestion.city}
                                    {suggestion.state && `, ${suggestion.state}`}
                                </p>
                            )}

                            {suggestion.isPickupDrop && suggestion.pickupDropType && (
                                <p className="text-xs text-green-600 mt-1 font-medium">
                                    <i className="fas fa-bus mr-1"></i>
                                    {type === 'from' ? 'Boarding Point' : 'Dropping Point'}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const PopularCityItem = ({ city, type, index }) => {
        return (
            <div
                key={`${type}-popular-${index}`}
                className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors duration-150 border-b border-gray-100 last:border-b-0"
                onClick={() => handleSuggestionSelect(type, {
                    ...city,
                    displayName: city.name,
                    original_name: city.name,
                })}
            >
                <div className="flex items-center">
                    <i className={`fas fa-map-marker-alt mr-3 ${type === 'from' ? 'text-blue-500' : 'text-orange-500'}`}></i>
                    <div>
                        <p className="font-medium text-gray-800 text-sm">
                            {city.name}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                            Popular City
                        </p>
                    </div>
                </div>
            </div>
        );
    };

    const RecentSearchItem = ({ search, type, index }) => {
        const isFrom = type === 'from';
        const displayName = isFrom ? search.fromName : search.toName;
        const city = isFrom ? search.fromCity : search.toCity;
        const original_name = isFrom ? search.from : search.to;
        const searchType = isFrom ? search.typeFrom : search.typeTo;

        return (
            <div
                key={`${search.id}-${type}`}
                className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors duration-150 border-b border-gray-100 last:border-b-0"
                onClick={() => {
                    // Pass the full search object to handleSuggestionSelect
                    handleSuggestionSelect(type, {
                        id: search.id,
                        displayName: displayName,
                        original_name: original_name,
                        city: city,
                        type: searchType,
                        isRecent: true,
                        searchData: search
                    });
                }}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-start flex-1">
                        <i className={`fas fa-history mt-1 mr-3 text-gray-400`}></i>
                        <div className="flex-1">
                            <p className="font-medium text-gray-800 text-sm">
                                {displayName}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                                {city} • {new Date(search.date).toLocaleDateString('en-GB', {
                                    day: 'numeric',
                                    month: 'short'
                                })}
                            </p>
                        </div>
                    </div>
                    <div className="ml-2">
                        <i className={`fas fa-arrow-right text-xs ${isFrom ? 'text-blue-400' : 'text-orange-400'}`}></i>
                    </div>
                </div>
            </div>
        );
    };

    useEffect(() => {
        const fetchPopularCities = async () => {
            if (hasFetched.current) return;
            try {
                hasFetched.current = true;
                const response = await fetch(api.cities);
                const result = await response.json();

                if (result.status && Array.isArray(result.data)) {
                    const popular = result.data.slice(0, 10);
                    setPopularCities(popular);

                    setSuggestions(prev => ({
                        from: {
                            ...prev.from,
                            popular: popular,
                            recent: prev.from.recent
                        },
                        to: {
                            ...prev.to,
                            popular: popular,
                            recent: prev.to.recent
                        }
                    }));
                }
            } catch (error) {
                hasFetched.current = false;
            }
        };

        fetchPopularCities();
    }, []);

    return (
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 ">
            {/* Header */}
            <div className="text-center h-[250px] md:h-[250px] relative overflow-hidden"
                style={{
                    backgroundImage: `url(${HeroBusImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}
            >
                <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-black/80 via-black/50 to-transparent" />
            </div>

            {/* Form Container */}
            <div className="max-w-6xl mx-auto transform translate-y-[-50px] px-4 ">
                <div className="bg-white rounded-2xl shadow-xl p-6">
                    <form onSubmit={handleSubmit}>
                        <div className="flex flex-col lg:flex-row items-center justify-between space-y-4 lg:space-y-0 lg:space-x-3">
                            {/* From - with suggestions */}
                            <div className="flex-1 w-full relative" ref={fromSuggestionsRef}>
                                <label className="block text-gray-600 text-xs font-medium mb-1">From</label>
                                <div className="relative" ref={fromInputRef}>
                                    <input
                                        type="text"
                                        value={formData.from}
                                        name="from"
                                        onChange={(e) => handleInputChange('from', e.target.value)}
                                        onFocus={() => {
                                            setShowSuggestions(prev => ({ ...prev, from: true }));
                                        }}
                                        className="w-full p-3 pl-10 border border-gray-300 rounded-lg text-sm font-medium focus:border-blue-500 focus:ring-1 focus:ring-blue-200 focus:outline-none"
                                        placeholder="Enter departure city"
                                    />
                                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                                        <i className="fas fa-map-marker-alt text-blue-500 text-sm"></i>
                                    </div>

                                    {loading.from && (
                                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                                        </div>
                                    )}
                                </div>

                                {/* Suggestions dropdown for From */}
                                {showSuggestions.from && (
                                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
                                        {/* Recent Searches - Show when no typing and have recent searches */}
                                        {!hasTyped.from && suggestions.from.recent.length > 0 && (
                                            <>
                                                <div className="sticky top-0 bg-gray-50 px-4 py-2 border-b flex justify-between items-center">
                                                    <p className="text-xs font-semibold text-gray-600">RECENT SEARCHES</p>
                                                    <button 
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            localStorage.removeItem('recentSearches');
                                                            setRecentSearches([]);
                                                            setSuggestions(prev => ({
                                                                from: { ...prev.from, recent: [] },
                                                                to: { ...prev.to, recent: [] }
                                                            }));
                                                        }}
                                                        className="text-xs text-gray-400 hover:text-red-500 px-2 py-1 rounded hover:bg-gray-100"
                                                    >
                                                        <i className="fas fa-trash-alt mr-1"></i>
                                                        Clear
                                                    </button>
                                                </div>
                                                {suggestions.from.recent.map((search, index) => (
                                                    <RecentSearchItem
                                                        key={`from-recent-${search.id}`}
                                                        search={search.searchData || search}
                                                        type="from"
                                                        index={index}
                                                    />
                                                ))}
                                            </>
                                        )}

                                        {/* Search Results Section */}
                                        {hasTyped.from && suggestions.from.search.length > 0 && (
                                            <>
                                                <div className="sticky top-0 bg-gray-50 px-4 py-2 border-b">
                                                    <p className="text-xs font-semibold text-gray-600">SEARCH RESULTS</p>
                                                </div>
                                                {suggestions.from.search.map((suggestion, index) => (
                                                    <SearchSuggestionItem
                                                        key={`from-search-${index}`}
                                                        suggestion={suggestion}
                                                        type="from"
                                                        index={index}
                                                    />
                                                ))}
                                            </>
                                        )}

                                        {/* Popular Cities Section */}
                                        {suggestions.from.popular.length > 0 && (
                                            <>
                                                <div className={`sticky ${(hasTyped.from && suggestions.from.search.length > 0) || suggestions.from.recent.length > 0 ? 'top-8' : 'top-0'} bg-gray-50 px-4 py-2 border-b`}>
                                                    <p className="text-xs font-semibold text-gray-600">POPULAR CITIES</p>
                                                </div>
                                                {suggestions.from.popular.map((city, index) => (
                                                    <PopularCityItem
                                                        key={`from-popular-${index}`}
                                                        city={city}
                                                        type="from"
                                                        index={index}
                                                    />
                                                ))}
                                            </>
                                        )}

                                        {/* No Results Message */}
                                        {hasTyped.from &&
                                            suggestions.from.search.length === 0 &&
                                            formData.from.length >= 2 &&
                                            !loading.from && (
                                                <div className="px-4 py-8 text-center">
                                                    <i className="fas fa-search text-gray-300 text-3xl mb-2"></i>
                                                    <p className="text-gray-500 text-sm">No cities found</p>
                                                    <p className="text-gray-400 text-xs mt-1">Try a different search term</p>
                                                </div>
                                            )}
                                    </div>
                                )}
                            </div>

                            {/* Swap Button */}
                            <div className="lg:-mx-1 z-10 hidden md:block">
                                <button
                                    type="button"
                                    onClick={swapLocations}
                                    className="mt-5 p-2 text-white rounded-full h-[40px] w-[40px] bg-gray-400 hover:bg-gray-500 transition-colors"
                                >
                                    <i className="fas fa-exchange-alt text-sm"></i>
                                </button>
                            </div>

                            {/* To - with suggestions */}
                            <div className="flex-1 w-full relative" ref={toSuggestionsRef}>
                                <label className="block text-gray-600 text-xs font-medium mb-1">To</label>
                                <div className="relative" ref={toInputRef}>
                                    <input
                                        type="text"
                                        value={formData.to}
                                        name="to"
                                        onChange={(e) => handleInputChange('to', e.target.value)}
                                        onFocus={() => {
                                            setShowSuggestions(prev => ({ ...prev, to: true }));
                                        }}
                                        className="w-full p-3 pl-10 border border-gray-300 rounded-lg text-sm font-medium focus:border-orange-500 focus:ring-1 focus:ring-orange-200 focus:outline-none"
                                        placeholder="Enter destination city"
                                    />
                                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                                        <i className="fas fa-map-marker-alt text-orange-500 text-sm"></i>
                                    </div>

                                    {loading.to && (
                                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-500"></div>
                                        </div>
                                    )}
                                </div>

                                {/* Suggestions dropdown for To */}
                                {showSuggestions.to && (
                                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
                                        {/* Recent Searches */}
                                        {!hasTyped.to && suggestions.to.recent.length > 0 && (
                                            <>
                                                <div className="sticky top-0 bg-gray-50 px-4 py-2 border-b flex justify-between items-center">
                                                    <p className="text-xs font-semibold text-gray-600">RECENT SEARCHES</p>
                                                    <button 
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            localStorage.removeItem('recentSearches');
                                                            setRecentSearches([]);
                                                            setSuggestions(prev => ({
                                                                from: { ...prev.from, recent: [] },
                                                                to: { ...prev.to, recent: [] }
                                                            }));
                                                        }}
                                                        className="text-xs text-gray-400 hover:text-red-500 px-2 py-1 rounded hover:bg-gray-100"
                                                    >
                                                        <i className="fas fa-trash-alt mr-1"></i>
                                                        Clear
                                                    </button>
                                                </div>
                                                {suggestions.to.recent.map((search, index) => (
                                                    <RecentSearchItem
                                                        key={`to-recent-${search.id}`}
                                                        search={search.searchData || search}
                                                        type="to"
                                                        index={index}
                                                    />
                                                ))}
                                            </>
                                        )}

                                        {/* Search Results Section */}
                                        {hasTyped.to && suggestions.to.search.length > 0 && (
                                            <>
                                                <div className="sticky top-0 bg-gray-50 px-4 py-2 border-b">
                                                    <p className="text-xs font-semibold text-gray-600">SEARCH RESULTS</p>
                                                </div>
                                                {suggestions.to.search.map((suggestion, index) => (
                                                    <SearchSuggestionItem
                                                        key={`to-search-${index}`}
                                                        suggestion={suggestion}
                                                        type="to"
                                                        index={index}
                                                    />
                                                ))}
                                            </>
                                        )}

                                        {/* Popular Cities Section */}
                                        {suggestions.to.popular.length > 0 && (
                                            <>
                                                <div className={`sticky ${(hasTyped.to && suggestions.to.search.length > 0) || suggestions.to.recent.length > 0 ? 'top-8' : 'top-0'} bg-gray-50 px-4 py-2 border-b`}>
                                                    <p className="text-xs font-semibold text-gray-600">POPULAR CITIES</p>
                                                </div>
                                                {suggestions.to.popular.map((city, index) => (
                                                    <PopularCityItem
                                                        key={`to-popular-${index}`}
                                                        city={city}
                                                        type="to"
                                                        index={index}
                                                    />
                                                ))}
                                            </>
                                        )}

                                        {/* No Results Message */}
                                        {hasTyped.to &&
                                            suggestions.to.search.length === 0 &&
                                            formData.to.length >= 2 &&
                                            !loading.to && (
                                                <div className="px-4 py-8 text-center">
                                                    <i className="fas fa-search text-gray-300 text-3xl mb-2"></i>
                                                    <p className="text-gray-500 text-sm">No cities found</p>
                                                    <p className="text-gray-400 text-xs mt-1">Try a different search term</p>
                                                </div>
                                            )}
                                    </div>
                                )}
                            </div>

                            {/* Date Picker */}
                            <div className="flex-1 w-full">
                                <label className="block text-gray-600 text-xs font-medium mb-1">Date of Journey</label>
                                <div className="relative">
                                    <Flatpickr
                                        ref={dateInputRef}
                                        options={dateOptions}
                                        value={formData.date}
                                        onChange={handleDateChange}
                                        className="w-full p-3 pl-10 border border-gray-300 rounded-lg text-sm font-medium focus:border-purple-500 focus:ring-1 focus:ring-purple-200 cursor-pointer focus:outline-none"
                                    />
                                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                                        <i className="fas fa-calendar-alt text-purple-500 text-sm"></i>
                                    </div>
                                </div>
                            </div>

                            {/* Search Button */}
                            <div className="flex-1 w-full">
                                <button
                                    type="submit"
                                    className="mt-5 w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold rounded-lg text-sm shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center space-x-2"
                                >
                                    <i className="fas fa-search text-sm"></i>
                                    <span>{modify ? 'Modify Search' : 'Search buses'}</span>
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default BookingForm;